import { useEffect, type ReactElement } from 'react';
import { Box, Button, Alert, Group, SegmentedControl } from '@mantine/core';
import ResponsiveRow from '@/shared/responsive/ResponsiveRow';
import { RefreshCw, LayoutGrid, Network } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import StatBand from '@/design/StatBand';
import { DirectoryPageViewModel } from './DirectoryPageViewModel';
import DirectoryToolbar from './DirectoryToolbar';
import DirectoryTableComponent from './DirectoryTableComponent';
import DirectoryOrgChart from './DirectoryOrgChart';
import DirectoryUserDrawerComponent from './DirectoryUserDrawerComponent';

export default function DirectoryPage(): ReactElement {
    const vm = useViewModel(DirectoryPageViewModel);

    useEffect(() => {
        void vm.initialize();
    }, [vm]);

    return (
        <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box style={{ flexShrink: 0 }}>
                <PageHeaderComponent
                    title="Directory"
                    subtitle="IDP-synced user directory across identity providers. Sync pulls the latest org structure from each connected source."
                    rightToolbar={
                        <Button
                            variant="filled"
                            size="xs"
                            leftSection={<RefreshCw size={14} />}
                            onClick={() => void vm.syncAll()}
                            loading={vm.syncing}
                            disabled={vm.syncing}
                        >
                            Sync directory
                        </Button>
                    }
                />
            </Box>

            {vm.syncMessage && (
                <Alert color="green" mt="sm" withCloseButton onClose={() => vm.dismissSyncBanner()}>
                    {vm.syncMessage}
                </Alert>
            )}
            {vm.syncError && (
                <Alert color="red" title="Sync failed" mt="sm" withCloseButton onClose={() => vm.dismissSyncBanner()}>
                    {vm.syncError}
                </Alert>
            )}
            {vm.loadError && (
                <Alert color="red" title="Failed to load directory" mt="sm">
                    {vm.loadError}
                </Alert>
            )}
            {vm.truncated && (
                <Alert color="yellow" title="Large directory" mt="sm">
                    Showing the first {vm.users.length.toLocaleString()} users. Narrow with a sync or contact support if you need the full set.
                </Alert>
            )}

            <Box mt="lg">
                <StatBand
                    stats={[
                        { label: 'Total users', value: vm.summary.total.toLocaleString() },
                        { label: 'Active', value: vm.summary.active.toLocaleString() },
                        { label: 'Inactive', value: vm.summary.inactive.toLocaleString() },
                        { label: 'Directory providers', value: vm.summary.sources.toLocaleString() },
                    ]}
                />
            </Box>

            <ResponsiveRow align={{ base: 'stretch', sm: 'flex-end' }} mt="lg" w="100%">
                <Box style={{ flex: 1, minWidth: 0 }}>
                    {vm.viewMode === 'org' && (
                        <DirectoryToolbar
                            searchTerm={vm.searchTerm}
                            onSearchChange={(v) => vm.setSearchTerm(v)}
                            statusFilter={vm.statusFilter}
                            onStatusChange={(v) => vm.setStatusFilter(v)}
                            departments={vm.availableDepartments}
                            departmentFilter={vm.departmentFilter}
                            onDepartmentChange={(v) => vm.setDepartmentFilter(v)}
                            connections={vm.connections}
                            sourceFilter={vm.sourceFilter}
                            onSourceChange={(v) => vm.setSourceFilter(v)}
                            hasActiveFilters={vm.hasActiveFilters}
                            onClear={() => vm.clearFilters()}
                        />
                    )}
                </Box>
                <SegmentedControl
                    size="xs"
                    value={vm.viewMode}
                    onChange={(v) => vm.setViewMode(v as 'grid' | 'org')}
                    data={[
                        { value: 'grid', label: (<Group gap={6} wrap="nowrap"><LayoutGrid size={13} /><span>Grid</span></Group>) },
                        { value: 'org', label: (<Group gap={6} wrap="nowrap"><Network size={13} /><span>Org chart</span></Group>) },
                    ]}
                />
            </ResponsiveRow>

            <Box mt="sm" style={{ flex: 1, minHeight: 0, marginBottom: 16, display: 'flex', flexDirection: 'column' }}>
                {vm.viewMode === 'org' ? (
                    <DirectoryOrgChart
                        users={vm.users}
                        filteredUsers={vm.filteredUsers}
                        filtersActive={vm.hasActiveFilters}
                        loading={vm.loading}
                        onOpenUser={(user) => vm.openDrawer(user)}
                    />
                ) : (
                    <Box style={{ flex: 1, minHeight: 0 }}>
                        <DirectoryTableComponent
                            users={vm.filteredUsers}
                            loading={vm.loading}
                            connectionLabel={(id) => vm.connectionLabel(id)}
                            onRowClick={(user) => vm.openDrawer(user)}
                        />
                    </Box>
                )}
            </Box>

            <DirectoryUserDrawerComponent
                user={vm.selectedUser}
                isOpen={vm.isDrawerOpen}
                sourceLabel={vm.selectedUser ? vm.connectionLabel(vm.selectedUser.connectionId) : ''}
                manager={vm.selectedUser ? vm.managerOf(vm.selectedUser) : null}
                reports={vm.selectedUser ? vm.reportsOf(vm.selectedUser) : []}
                onOpenUser={(u) => vm.openDrawer(u)}
                onClose={() => vm.closeDrawer()}
            />
        </Box>
    );
}
